<template>
  <div class="container">
    <Loading :loading="loading" />
    <v-widget
      :title="$t('securities_transfer_request')"
      padding-top="pt-0"
      fluid
    >
      <template #content>
        <v-stepper v-model="step" alt-labels flat class="elevation-0">
          <v-stepper-header elevation="0" class="elevation-0">
            <template v-for="(item, i) in steps">
              <v-stepper-step
                :key="`${i}-step`"
                :complete="step > i + 1"
                :step="i + 1"
              >
                <span class="primary--text text-center">{{
                  $t(item.title)
                }}</span>
              </v-stepper-step>
              <v-divider :key="i" v-if="i + 1 < steps.length" />
            </template>
          </v-stepper-header>
          <!-- COMPONENTS -->
          <v-stepper-items>
            <v-stepper-content
              v-for="(item, i) in steps"
              :key="`step-content-${i}`"
              :step="i + 1"
              class="mt-0 pa-0"
            >
              <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
                <component
                  :is="item.component"
                  v-bind="{
                    securityTransfer
                  }"
                  :canPrevious="step > 1"
                  :canNext="step < steps.length"
                  :canSubmit="step === steps.length"
                  @next="handleSubmit(handleNext)"
                  @previous="handlePrevious"
                  @submit="handleSubmit(handleFormSubmit)"
                  @cancel="handleCancel"
                />
              </ValidationObserver>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </template>
    </v-widget>
  </div>
</template>

<script>
import BaseForm from '@/components/commons/BaseForm'
import VWidget from '@/components/commons/VWidget'
import Type from './partials/wizard/Type'
import Form from './partials/wizard/Form'
import Observations from './partials/wizard/Observations'
import Finish from './partials/wizard/Finish'
import { mapActions, mapState } from 'vuex'
import PublicSettingService from '@/services/publicSetting'
import TwoFactorModal from '@/components/commons/TwoFactorModal'
import Loading from '@/components/commons/Loading'
import SecurityTransferService from '@/services/securityTransferRequest'

export default {
  components: {
    BaseForm,
    VWidget,
    Loading,
    Type,
    Form,
    Observations,
    Finish
  },
  data() {
    return {
      stepsItems: [
        {
          title: 'type_of_transfer',
          component: 'Type'
        },
        {
          title: 'security_transfer_data',
          component: 'Form'
        },
        {
          title: 'security_transfer_authorization',
          component: 'Observations'
        },
        {
          title: 'transfer_finalize',
          component: 'Finish'
        }
      ],
      states: [],
      step: 1,
      loading: false,
      securityTransfer: {
        transferType: 'Receive',
        accountCode: '',
        beneficiaryName: '',
        financialInstitution: '',
        accountNumber: '',
        observations: '',
        securities: [],
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        transferSupportUrl: '',
        transferApprovalUrl: '',
        isActive: true,
        isThirdParty: false,
        status: 'Nuevo'
      }
    }
  },
  async created() {
    if (this.handleAuthorityAccounts() && !this.isExecutive) {
      this.$router.push({ name: 'Home' })
    }

    await this.getPublicSettings()
    let twoFactor = this.settings.find(
      item => item.key === '2fa_transfer_requests'
    )

    if (twoFactor.value && !this.user.twofactor_habilitated) {
      let response = await this.handleTwoFactorModal(
        this.user.twofactor_habilitated
      )
      if (!response) {
        this.loading = true
        this.$dialog.message.error(this.$t('twofactor_required'))
        this.$router.push({ name: 'Home' })
      }
    }
  },
  computed: {
    ...mapState('account', ['account', 'authorityAccounts', 'accountData']),
    ...mapState('transfer', ['transferRedirect', 'clientRequestData']),
    ...mapState('setting', ['settings']),
    ...mapState('auth', ['user']),
    isExecutive() {
      return (
        (this.user?.authorityLevelsCode === 1 ||
          this.user?.authorityLevelsCode === 2 ||
          this.user?.authorityLevelsCode === 4) &&
        !this.isAccountCode
      )
    },
    isAccountCode() {
      return (
        this.user?.avobjects?.findIndex(
          item =>
            item.code === this.account.code && item.typeCode === 'accountCode'
        ) > -1
      )
    },
    canPrevious() {
      return this.step > 1
    },
    // Dynamic Menu
    steps() {
      return this.stepsItems
    }
  },
  methods: {
    ...mapActions('transfer', [
      'setPermanentInstruction',
      'setTransferRedirect'
    ]),
    ...mapActions('setting', ['setSettings']),

    handleAuthorityAccounts() {
      return (
        this.authorityAccounts.findIndex(
          item => item.code == this.account.code
        ) > -1
      )
    },
    handleNext() {
      this.step = this.step + 1
    },
    handlePrevious() {
      this.step = this.step - 1
    },
    async handleFormSubmit() {
      try {
        let twoFactor = this.settings.find(
          item => item.key === '2fa_transfer_requests'
        )
        if (twoFactor.value && this.user.twofactor_habilitated) {
          const response = await this.handleTwoFactorModal(
            this.user.twofactor_habilitated
          )

          if (response == 'reset') {
            this.$dialog.message.error(this.$t('twofactor_required'))
            this.$router.push({
              name: 'Delete',
              query: {
                redirect: 'traspaso-titulo/create'
              }
            })
            throw new Error(this.$t('twofactor_required'))
          }
          if (!response) {
            return
          }
        }

        this.loading = true

        // Assign account code
        this.securityTransfer.accountCode = this.account.code

        const payload = {
          ...this.securityTransfer,
          isExecutive: this.isExecutive
        }

        const request = await SecurityTransferService.post(
          this.account.code,
          payload
        )

        if (request.status == 200) {
          this.$dialog.notify.success(this.$t('transfer_request_success'))
          this.$router.push({ name: 'SecurityTransfer' })
        } else {
          this.$dialog.notify.error(this.$t('transfer_request_error'))
        }
      } catch (error) {
        console.log(error)
        this.$dialog.notify.error(this.$t('transfer_request_error'))
      } finally {
        this.loading = false
      }
    },
    handleCancel() {
      this.$router.push({ name: 'SecurityTransfer' })
    },
    async handleTwoFactorModal(twofactor) {
      const params = {
        fullscreen: this.$vuetify.breakpoint.smAndDown,
        width: twofactor ? '40%' : '80%',
        transition: 'dialog-top-transition',
        persistent: false,
        hideOverlay: false,
        twoFactor: twofactor,
        hideSubmit: true,
        showClose: twofactor,
        showSnackbar: false
      }

      return await this.$dialog.showAndWait(TwoFactorModal, params)
    },
    async getPublicSettings() {
      const { data } = await PublicSettingService.fetch({})
      this.setSettings(data)
    }
  }
}
</script>
<style scoped>
@media screen and (min-width: 960px) {
  .v-stepper--alt-labels .v-stepper__header {
    height: 110px !important;
  }
}
</style>
